import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    FileText, RefreshCw, Filter, ChevronRight, ChevronDown, Database,
    Sparkles, AlertTriangle, Layers, Cpu, Trash2, Eye, Link2,
    CheckCircle2, ShieldCheck, Undo2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft: "bg-white/5 border-white/20 text-white/60",
    needs_review: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    approved: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    superseded: "bg-white/[0.03] border-white/10 text-white/30",
};

const SOURCE_TONE = {
    protocol: "border-violet-400/40 text-violet-300 bg-violet-400/5",
    edc_guidelines: "border-sky-400/40 text-sky-300 bg-sky-400/5",
};

export default function GeneratedFormsPage() {
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStudy, setFilterStudy] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [onlyCurrent, setOnlyCurrent] = useState(true);
    const [openPkgId, setOpenPkgId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStudy) params.set("study_id", filterStudy);
            if (filterStatus) params.set("status", filterStatus);
            if (onlyCurrent) params.set("only_current", "true");
            const { data } = await api.get(`/v1/forms/packages?${params.toString()}`);
            setPackages(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setLoading(false); }
    }, [filterStudy, filterStatus, onlyCurrent]);

    useEffect(() => { load(); }, [load]);

    const studies = useMemo(() => {
        const set = new Set();
        for (const p of packages) if (p.studyID) set.add(p.studyID);
        return Array.from(set).sort();
    }, [packages]);

    const onArchive = async (pkg) => {
        if (!confirm(`Soft-archive package ${pkg.generatedFormPackageVersion} for ${pkg.studyID}?`)) return;
        try {
            await api.delete(`/v1/forms/packages/${pkg.id}`);
            toast.success("Archived");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const onApprove = async (pkg) => {
        const reason = prompt(
            `Approve package ${pkg.generatedFormPackageVersion} for ${pkg.studyID}?\n\n` +
            "Reason / clinical rationale (required, ≥ 3 chars):",
            "Reviewed visit-by-visit, fields traced, conflicts resolved.",
        );
        if (!reason || reason.trim().length < 3) {
            if (reason !== null) toast.error("Approval cancelled — a reason ≥ 3 chars is required.");
            return;
        }
        try {
            const { data } = await api.post(`/v1/forms/packages/${pkg.id}/approve`, {
                reason: reason.trim(),
            });
            if (data.already_approved) {
                toast.info(`Already approved by ${data.approved_by_email} on ${data.approved_at?.slice(0, 10)}`);
            } else {
                toast.success(`Approved by ${data.approved_by_email}. Package is now LOCKED.`);
            }
            load();
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail);
            if (e?.response?.status === 403) toast.error("4-eyes violation: a different admin must approve.");
            else toast.error(msg);
        }
    };

    const onReject = async (pkg) => {
        const reason = prompt(
            `Reject approved package ${pkg.generatedFormPackageVersion}?\n` +
            "(Requires super_admin. The package will drop back to needs_review.)\n\n" +
            "Reason (required, ≥ 3 chars):",
            "Requires re-review of conflict resolution.",
        );
        if (!reason || reason.trim().length < 3) {
            if (reason !== null) toast.error("Rejection cancelled — a reason ≥ 3 chars is required.");
            return;
        }
        try {
            await api.post(`/v1/forms/packages/${pkg.id}/reject`, { reason: reason.trim() });
            toast.success("Rejected — back to needs_review");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    return (
        <div className="px-8 py-8" data-testid="generated-forms-page">
            <div className="flex items-start justify-between mb-7">
                <div>
                    <h1 className="text-2xl font-display tracking-tight mb-1">Generated Forms</h1>
                    <p className="text-sm text-white/50 max-w-3xl">
                        Versioned form packages produced by the Form Composer Flow (Protocol × EDC Guidelines).
                        These are <strong>read-only artifacts</strong> retrieved from the datalake — they're never regenerated here.
                        To generate or re-generate a package, go to <Link to="/qa/form-generation" className="underline text-white/70">QA › Form Generation</Link>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        data-testid="forms-refresh-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                    >
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/qa/form-generation")}
                        data-testid="forms-generate-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Generate package
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <Pill icon={Database} title="Datalake" value="generated_form_packages" sub="versioned, immutable history" tone="emerald" />
                <Pill icon={Cpu} title="Read agent" value="forms-retrieval" sub="deterministic · no LLM" tone="sky" />
                <Pill icon={Layers} title="Read flow" value="get-forms" sub="published · prod · metered" tone="violet" />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-md border border-white/10 bg-white/[0.02]">
                <Filter className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />
                <select
                    value={filterStudy}
                    onChange={(e) => setFilterStudy(e.target.value)}
                    data-testid="forms-filter-study"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">All studies</option>
                    {studies.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="forms-filter-status"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">Any status</option>
                    <option value="draft">draft</option>
                    <option value="needs_review">needs_review</option>
                    <option value="approved">approved</option>
                    <option value="superseded">superseded</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs text-white/60 ml-2">
                    <input type="checkbox" checked={onlyCurrent}
                        onChange={(e) => setOnlyCurrent(e.target.checked)}
                        data-testid="forms-only-current" />
                    Only current versions
                </label>
                <span className="ml-auto text-xs text-white/40">{packages.length} package{packages.length === 1 ? "" : "s"}</span>
            </div>

            {loading ? (
                <div className="text-white/40 text-sm">Loading…</div>
            ) : packages.length === 0 ? (
                <div className="p-10 rounded-md border border-dashed border-white/15 text-center" data-testid="forms-empty-state">
                    <FileText className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-white/60 mb-1">No form packages yet</div>
                    <div className="text-xs text-white/40 mb-4">
                        Generate the first package via QA › Form Generation.
                    </div>
                    <Link
                        to="/qa/form-generation"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />Open QA › Form Generation
                    </Link>
                </div>
            ) : (
                <div className="space-y-2">
                    {packages.map((pkg) => (
                        <PackageRow
                            key={pkg.id}
                            pkg={pkg}
                            opened={openPkgId === pkg.id}
                            onToggle={() => setOpenPkgId(openPkgId === pkg.id ? null : pkg.id)}
                            onArchive={() => onArchive(pkg)}
                            onApprove={() => onApprove(pkg)}
                            onReject={() => onReject(pkg)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function Pill({ icon: Icon, title, value, sub, tone }) {
    const toneClass = {
        violet: "border-violet-400/20 bg-violet-400/[0.04] text-violet-300",
        sky: "border-sky-400/20 bg-sky-400/[0.04] text-sky-300",
        emerald: "border-emerald-400/20 bg-emerald-400/[0.04] text-emerald-300",
    }[tone] || "border-white/10";
    return (
        <div className={`flex items-start gap-3 p-3 rounded-md border ${toneClass}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide opacity-70">{title}</div>
                <div className="text-sm font-mono truncate">{value}</div>
                <div className="text-xs opacity-50">{sub}</div>
            </div>
        </div>
    );
}

function PackageRow({ pkg, opened, onToggle, onArchive, onApprove, onReject }) {
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [visitFilter, setVisitFilter] = useState("");
    const [formFilter, setFormFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");

    const fetchDetail = useCallback(async () => {
        setLoadingDetail(true);
        try {
            const params = new URLSearchParams();
            if (visitFilter) params.set("visit", visitFilter);
            if (formFilter) params.set("form", formFilter);
            if (categoryFilter) params.set("category", categoryFilter);
            const path = params.toString()
                ? `/v1/forms/package/${pkg.id}?${params.toString()}`
                : `/v1/forms/package/${pkg.id}`;
            const { data } = await api.get(path);
            setDetail(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setLoadingDetail(false); }
    }, [pkg.id, visitFilter, formFilter, categoryFilter]);

    useEffect(() => {
        if (opened && !detail) fetchDetail();
    }, [opened, detail, fetchDetail]);

    return (
        <div className="rounded-md border border-white/10 bg-white/[0.02]" data-testid={`package-row-${pkg.id}`}>
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-white/[0.03]"
            >
                {opened ? <ChevronDown className="w-4 h-4 text-white/40" strokeWidth={1.5} /> : <ChevronRight className="w-4 h-4 text-white/40" strokeWidth={1.5} />}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white">{pkg.studyID}</span>
                        <span className="text-white/30">·</span>
                        <span className="font-mono text-xs text-white/60">protocol {pkg.protocolVersion}</span>
                        <span className="text-white/30">×</span>
                        <span className="font-mono text-xs text-white/60">EDC {pkg.edcGuidelinesVersion}</span>
                        <span className="text-white/30">→</span>
                        <span className="font-mono text-xs text-white">pkg {pkg.generatedFormPackageVersion}</span>
                        {pkg.is_current_version && (
                            <span className="px-1.5 py-0.5 rounded border border-emerald-400/30 text-emerald-400 text-[10px]">CURRENT</span>
                        )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-white/50">
                        <span>{pkg.visit_count} visits · {pkg.form_count} forms · {pkg.field_count} fields</span>
                        <span className="font-mono text-[10px] text-white/40">agent v{pkg.agent_version_no} · flow v{pkg.flow_version_no}</span>
                        <span className="font-mono text-[10px] text-white/40">${(pkg.llm_cost_usd || 0).toFixed(4)}</span>
                    </div>
                </div>
                <span className={`px-1.5 py-0.5 rounded border text-[10px] ${STATUS_BADGE[pkg.generationStatus] || ""}`}>
                    {pkg.generationStatus}
                </span>
                {pkg.generationStatus === "approved" && (
                    <span
                        className="flex items-center gap-1 text-[10px] text-emerald-400"
                        title={`Approved by ${pkg.approved_by_email} · ${pkg.approval_reason}`}
                    >
                        <ShieldCheck className="w-3 h-3" strokeWidth={1.75} />locked
                    </span>
                )}
                {/* Approval action — hide when already approved or superseded */}
                {pkg.generationStatus !== "approved" && pkg.generationStatus !== "superseded" && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
                        title="Approve (4-eyes — approver must differ from generator)"
                        data-testid={`package-approve-${pkg.id}`}
                        className="p-1 rounded hover:bg-emerald-400/10 text-white/50 hover:text-emerald-400"
                    >
                        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                )}
                {pkg.generationStatus === "approved" && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onReject?.(); }}
                        title="Reject (super_admin only — drops back to needs_review)"
                        data-testid={`package-reject-${pkg.id}`}
                        className="p-1 rounded hover:bg-amber-400/10 text-white/50 hover:text-amber-400"
                    >
                        <Undo2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onArchive(); }}
                    title="Archive"
                    data-testid={`package-archive-${pkg.id}`}
                    className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-red-400"
                >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
            </button>

            {opened && (
                <div className="border-t border-white/10 p-4 space-y-3">
                    {/* Per-package filter row — applied via the Get Forms endpoint */}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-white/40">Filter (re-fetches via</span>
                        <span className="font-mono text-violet-300">GET /v1/forms/package/{pkg.id.slice(0, 6)}…</span>
                        <span className="text-white/40">):</span>
                        <input
                            type="text" placeholder="visit name"
                            value={visitFilter}
                            onChange={(e) => setVisitFilter(e.target.value)}
                            data-testid={`pkg-filter-visit-${pkg.id}`}
                            className="bg-[#050505] border border-white/15 rounded px-2 py-1 font-mono text-[11px] w-32"
                        />
                        <input
                            type="text" placeholder="form name"
                            value={formFilter}
                            onChange={(e) => setFormFilter(e.target.value)}
                            data-testid={`pkg-filter-form-${pkg.id}`}
                            className="bg-[#050505] border border-white/15 rounded px-2 py-1 font-mono text-[11px] w-32"
                        />
                        <input
                            type="text" placeholder="category,csv"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            data-testid={`pkg-filter-category-${pkg.id}`}
                            className="bg-[#050505] border border-white/15 rounded px-2 py-1 font-mono text-[11px] w-36"
                        />
                        <button
                            type="button"
                            onClick={() => { setDetail(null); fetchDetail(); }}
                            disabled={loadingDetail}
                            data-testid={`pkg-filter-apply-${pkg.id}`}
                            className="px-2 py-1 rounded border border-violet-400/30 text-violet-300 text-[11px] hover:bg-violet-400/10"
                        >
                            Apply
                        </button>
                        {detail?.filtered && (
                            <span className="text-amber-400 text-[10px]">
                                filtered · {detail.filtered_visit_count}v / {detail.filtered_form_count}f / {detail.filtered_field_count} fields
                            </span>
                        )}
                    </div>

                    {loadingDetail || !detail ? (
                        <div className="text-white/40 text-sm">Loading…</div>
                    ) : (
                        <PackageDetail pkg={detail} />
                    )}
                </div>
            )}
        </div>
    );
}

function PackageDetail({ pkg }) {
    const conflicts = pkg.conflicts || [];
    const reviewItems = pkg.humanReviewItems || [];
    const globalWarnings = pkg.globalWarnings || [];

    return (
        <div className="space-y-3" data-testid={`package-detail-${pkg.id}`}>
            {(conflicts.length + reviewItems.length + globalWarnings.length) > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {conflicts.length > 0 && <Panel title="Conflicts" tone="red" count={conflicts.length}>
                        {conflicts.map((c, i) => (
                            <div key={i} className="text-[11px] py-1 border-b border-white/5 last:border-b-0">
                                <div className="font-mono text-red-300 truncate">{c.field}</div>
                                <div className="text-white/60">P: <span className="font-mono">{String(c.protocolValue ?? "—")}</span> / E: <span className="font-mono">{String(c.edcValue ?? "—")}</span></div>
                            </div>
                        ))}
                    </Panel>}
                    {reviewItems.length > 0 && <Panel title="Human review items" tone="amber" count={reviewItems.length}>
                        {reviewItems.map((r, i) => (
                            <div key={i} className="text-[11px] py-1 border-b border-white/5 last:border-b-0">
                                <span className="font-mono text-amber-300">{r.category || "general"}</span>
                                <div className="text-white/70">{r.details}</div>
                            </div>
                        ))}
                    </Panel>}
                    {globalWarnings.length > 0 && <Panel title="Global warnings" tone="white" count={globalWarnings.length}>
                        {globalWarnings.map((w, i) => (
                            <div key={i} className="text-[11px] text-white/60 py-0.5">· {typeof w === "string" ? w : JSON.stringify(w)}</div>
                        ))}
                    </Panel>}
                </div>
            )}
            <div className="space-y-2">
                {(pkg.visits || []).map((v, i) => (
                    <VisitBlock key={`${v.visitID || i}`} visit={v} />
                ))}
            </div>
        </div>
    );
}

function Panel({ title, tone, count, children }) {
    const cls = { red: "border-red-400/30 bg-red-400/5", amber: "border-amber-400/30 bg-amber-400/5", white: "border-white/15 bg-white/[0.02]" }[tone];
    return (
        <div className={`rounded border ${cls} p-2 max-h-56 overflow-y-auto`}>
            <div className="flex items-center gap-1.5 mb-1 text-xs">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.75} />
                <span className="font-medium">{title}</span>
                <span className="text-white/40">{count}</span>
            </div>
            {children}
        </div>
    );
}

function VisitBlock({ visit }) {
    const [open, setOpen] = useState(false);
    const forms = visit.forms || [];
    return (
        <div className="rounded border border-white/10 bg-white/[0.02]">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03]"
            >
                {open ? <ChevronDown className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} /> : <ChevronRight className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />}
                <span className="font-mono text-xs text-white/50">#{visit.visitOrder || "?"}</span>
                <span className="text-sm text-white">{visit.visitName || visit.visitID}</span>
                <span className="ml-auto text-xs text-white/40">{forms.length} form{forms.length === 1 ? "" : "s"}</span>
            </button>
            {open && (
                <div className="border-t border-white/5 px-3 py-2 space-y-1.5">
                    {forms.length === 0 ? (
                        <div className="text-xs text-white/30">No forms for this visit (under current filter).</div>
                    ) : (
                        forms.map((f, i) => <FormBlock key={`${f.formID || i}-${i}`} form={f} />)
                    )}
                </div>
            )}
        </div>
    );
}

function FormBlock({ form }) {
    const [open, setOpen] = useState(false);
    const fields = form.fields || [];
    const sources = form.source || [];
    return (
        <div className="rounded border border-white/5 bg-white/[0.01]">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/[0.03]"
            >
                {open ? <ChevronDown className="w-3 h-3 text-white/30" strokeWidth={1.5} /> : <ChevronRight className="w-3 h-3 text-white/30" strokeWidth={1.5} />}
                <span className="text-sm text-white truncate">{form.formName}</span>
                {form.formCategory && <span className="text-[10px] font-mono text-violet-300">{form.formCategory}</span>}
                {sources.map((s, i) => (
                    <span key={i} className={`px-1 py-0.5 rounded border text-[9px] ${SOURCE_TONE[s] || "border-white/20"}`}>{s}</span>
                ))}
                <span className="ml-auto text-[10px] text-white/40">{fields.length} fields</span>
            </button>
            {open && (
                <div className="border-t border-white/5 px-3 py-1.5 space-y-0.5">
                    {fields.map((f, i) => <FieldRow key={`${f.fieldID || f.fieldName || i}-${i}`} field={f} />)}
                </div>
            )}
        </div>
    );
}

function FieldRow({ field }) {
    const [showTrace, setShowTrace] = useState(false);
    const trace = field.sourceTraceability || [];
    return (
        <div className="text-[11px] py-0.5">
            <div className="grid grid-cols-12 gap-2">
                <span className="col-span-4 text-white/80 truncate">{field.fieldLabel || field.fieldName}</span>
                <span className="col-span-2 font-mono text-white/40">{field.fieldType}</span>
                <span className="col-span-1 text-red-400">{field.required ? "req" : ""}</span>
                <span className="col-span-1 text-white/40">{field.units || ""}</span>
                <span className="col-span-2 flex items-center gap-1">
                    {trace.map((t, i) => (
                        <span key={i} className={`px-1 rounded border text-[9px] ${SOURCE_TONE[t.sourceType] || ""}`}>
                            {t.sourceType === "protocol" ? "P" : "E"}
                        </span>
                    ))}
                    {field.humanReviewRequired && <AlertTriangle className="w-2.5 h-2.5 text-amber-400" strokeWidth={1.75} />}
                </span>
                <span className="col-span-2 text-right">
                    <button type="button" onClick={() => setShowTrace(!showTrace)} className="text-[10px] text-white/40 hover:text-white">
                        <Link2 className="w-2.5 h-2.5 inline mr-0.5" strokeWidth={1.5} />{trace.length} source{trace.length === 1 ? "" : "s"}
                    </button>
                </span>
            </div>
            {showTrace && trace.map((t, i) => (
                <div key={i} className="ml-4 mt-1 text-[10px] text-white/50 border-l border-white/10 pl-2">
                    <strong>{t.sourceType}</strong> ({t.sourceVersion || "?"})
                    {t.section && <span className="ml-2">§{t.section}</span>}
                    {t.page && <span className="ml-2">p.{t.page}</span>}
                    {t.quotedRequirement && <div className="italic text-white/60 mt-0.5">"{t.quotedRequirement}"</div>}
                </div>
            ))}
        </div>
    );
}
